/** @jsx jsx */
import {
  BookmarkIcon,
  Button,
  Box,
  DownloadIcon,
  Grid,
  Link,
  SemiTitle,
  Stack,
  Text,
  Title,
} from '@bottlebooks/gatsby-theme-base';
import { useFavorites as useBookmarks } from '@bottlebooks/gatsby-theme-event/plugins/gatsby-plugin-favorites/src';
import { DownloadMyTastingBooklet } from '@bottlebooks/gatsby-theme-event/plugins/gatsby-plugin-favorites/src/components/CatalogExportDialog';
// import FeatureOverview from '@bottlebooks/gatsby-theme-event/plugins/gatsby-plugin-favorites/src/components/FeatureOverview';
import useAuthentication from '@bottlebooks/gatsby-theme-event/plugins/gatsby-plugin-firebase-auth/src/useAuthentication';
import useUser from '@bottlebooks/gatsby-theme-event/plugins/gatsby-plugin-firebase-auth/src/useUser';
import { useTastingNotes } from '@bottlebooks/gatsby-theme-event/plugins/gatsby-plugin-tastingnotes/src';
import useLink from '@bottlebooks/gatsby-theme-event/src/useLink';
import useLocale from '@bottlebooks/gatsby-theme-event/src/useLocale';
import { Plural, Trans } from '@lingui/macro';
import React from 'react';
import { jsx } from 'theme-ui';
import HeartIcon from '../../../gatsby-design-system/components/icons/StarIcon';
import FeatureOverview from './FeatureOverview';
export default function MyBookmarksAnnouncement({ ...rest }) {
  const { isAuthenticated } = useAuthentication();
  if (isAuthenticated()) {
    return <WelcomeBack {...rest} />;
  }

  return <NotLoggedIn {...rest} />;
}

function NotLoggedIn({ ...rest }) {
  const { signIn } = useAuthentication();
  return (
    <Grid columns={12} {...rest}>
      <Stack alignItems="flex-start" sx={{ gridColumn: ['1/-1', '3/-3'] }}>
        <FeatureOverview />
        <Button
          variant="primary"
          onClick={() => {
            signIn();
          }}
        >
          <Trans>Create account</Trans>
        </Button>
      </Stack>
    </Grid>
  );
}

function WelcomeBack({ ...rest }) {
  const [user] = useUser();
  const userName = user?.displayName;
  const locale = useLocale();
  const { favorites: bookmarks, isLoading: isLoadingBookmarks } =
    useBookmarks();
  const { all: tastingNotes, isLoading: isLoadingTastingNotes } =
    useTastingNotes();
  const link = useLink();

  return (
    <Box {...rest}>
      <Title>
        <Trans>
          Welcome, <Link to={`/${locale}/my/bookmarks`}>{userName}!</Link>
        </Trans>
      </Title>

      <Grid columns={[1, 2]}>
        <Box>
          <SemiTitle>
            <Trans>Your bookmarks</Trans>
          </SemiTitle>
          {isLoadingBookmarks ? (
            <Text>
              <Trans>Loading…</Trans>
            </Text>
          ) : bookmarks?.length ? (
            <Stack alignItems="flex-start">
              <Text>
                <Plural
                  value={bookmarks?.length}
                  one="You have one bookmark."
                  other="You have # bookmarks."
                />
              </Text>
              <Button variant="outline" to={link.bookmarks()}>
                <BookmarkIcon variant="hollow" />
                <Text>
                  <Trans>View my bookmarks</Trans>
                </Text>
              </Button>
              <DownloadButton />
            </Stack>
          ) : (
            <Stack alignItems="flex-start">
              <Text>
                <Trans>You don't have any bookmarks yet.</Trans>
              </Text>
              <Button to={link.exhibitors()} variant="outline">
                <Trans>
                  Start by adding some exhibitors to your bookmark list.
                </Trans>
              </Button>
            </Stack>
          )}
        </Box>
        <Box>
          <SemiTitle>
            <Trans>Your tasting notes</Trans>
          </SemiTitle>
          {isLoadingTastingNotes ? (
            <Text>
              <Trans>Loading…</Trans>
            </Text>
          ) : tastingNotes?.length ? (
            <Stack alignItems="flex-start">
              <Text>
                <Plural
                  value={tastingNotes?.length}
                  one="You have one tasting note."
                  other="You have # tasting notes."
                />
              </Text>
              <Button to={link.tastingNotes()} variant="outline">
                <HeartIcon variant="hollow" />
                <Trans>View my tasting notes</Trans>
              </Button>
            </Stack>
          ) : (
            <Stack alignItems="flex-start">
              <Text>
                <Trans>You don't have any tasting notes yet.</Trans>
              </Text>
              <Button to={link.products()} variant="outline">
                <Trans>Add notes to some products you tasted.</Trans>
              </Button>
            </Stack>
          )}
        </Box>
      </Grid>
    </Box>
  );
}

function DownloadButton({ ...rest }) {
  const [isRendering, setIsRendering] = React.useState(false);

  return (
    <>
      {isRendering ? (
        <DownloadMyTastingBooklet {...rest} />
      ) : (
        <Link
          onClick={() => setIsRendering(true)}
          sx={{ display: 'flex' }}
          {...rest}
        >
          <DownloadIcon sx={{ marginRight: 1 }} />
          <Text>
            <Trans>Download my tasting booklet</Trans>
          </Text>
        </Link>
      )}
    </>
  );
}
